import React, { Component } from "react";
import { FormattedMessage, injectIntl, type IntlShape } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";

import { fetchWithAuth } from "~/common/fetch";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { API_PROMO_MATERIALS_GET } from "~/config";
import { setDocumentTitle } from "~/hooks/use-document-title";
import { selectUser } from "~/slices/user";
import type { RootState } from "~/store";

import PromoMaterialsCard from "./promo_materials_card";

const style = {
  // Styles are in CSS class .promo-materials-grid
} as const;

const mapStateToProps = (state: RootState) => ({
  user: selectUser(state),
});

type PromoMaterialsPageProps = PropsFromRedux & { intl: IntlShape; bareLayout?: boolean };

type PromoMaterialsPageState = {
  loading: boolean;
  materials: TPromoMaterial[];
};

export type TPromoMaterial = {
  promo_material_id: number;
  title: string;
  thumbnail: string;
  item_type: 1 | 2; // 1=file, 2=module
  source_type: 1 | 2; // 1=file, 2=url (for file type)
  filename?: string;
  url?: string;
  module_path?: string;
  downloadable: boolean;
};

class PromoMaterialsPage extends Component<PromoMaterialsPageProps, PromoMaterialsPageState> {
  constructor(props: PromoMaterialsPageProps) {
    super(props);
    this.state = {
      materials: [],
      loading: true,
    };
  }

  componentDidMount() {
    const { user } = this.props;

    fetchWithAuth(API_PROMO_MATERIALS_GET)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Error fetching promo materials");
        }
        return response.text();
      })
      .then((text) => JSON.parse(text))
      .then((json) => this.setState({ materials: json, loading: false }))
      .catch(() => this.setState({ loading: false }));

    _paq.push(["HeatmapSessionRecording::enable"]);
    if (user?.account_id) {
      _paq.push(["setUserId", user.account_id.toString()]);
    }
    setDocumentTitle(this.props.intl.formatMessage({ id: "promo.title" }));
  }

  renderCards(materials: TPromoMaterial[]) {
    const { loading } = this.state;

    if (loading) {
      return <Loader />;
    }

    if (materials?.length > 0) {
      return materials.map((material, index) => <PromoMaterialsCard material={material} key={material.promo_material_id} index={index} />);
    }

    return <FormattedMessage id="promo.empty" />;
  }

  render() {
    return (
      <Layout chrome={!this.props.bareLayout}>
        <Portlet as="main">
          <PortletTitle iconClassName="icon-heart" id="promo-materials-page">
            <FormattedMessage id="promo.title" />
          </PortletTitle>

          <div className="portlet-body">
            <div className="promo-materials-grid">{this.renderCards(this.state.materials)}</div>
          </div>
        </Portlet>
      </Layout>
    );
  }
}

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(PromoMaterialsPage));
